/* ============================================================================
   ALIA · VINYL — 10-vinyl.js
   ----------------------------------------------------------------------------
   THE ONLY SCRIPT THIS THEME SHIPS. Loaded with `defer` from layout/theme.liquid
   after gsap 3.13.0 / ScrollTrigger / SplitText / lenis 1.3.4, all also `defer`,
   so execution order is guaranteed and the DOM is parsed before anything runs.

   Motion architecture — do not fragment this file:
     · ONE Lenis instance, created once at boot and never re-created.
     · ONE ScrollTrigger registry spanning every section, synced to that Lenis.
     · ONE requestAnimationFrame spin loop shared by the hero and deck records.
     · ONE countdown interval feeding both the hero readouts and Last Call.

   Theme editor support:
     · Every ScrollTrigger, tween, matchMedia and interval is filed under the
       name of the section that created it (see REG / keep / killKey).
     · shopify:section:unload kills exactly that section's own work.
     · shopify:section:load rebuilds only that section, re-resolves the
       cross-section bindings, then calls ScrollTrigger.refresh() so pin
       spacing survives injection and reordering.
     · The preloader never re-runs after boot — a re-injected chrome section is
       dismissed on sight instead of covering the site with a full-screen veil.
   ========================================================================== */
(function () {
  "use strict";

  var HAS_GSAP  = typeof gsap !== "undefined";
  var HAS_ST    = typeof ScrollTrigger !== "undefined";
  var HAS_SPLIT = typeof SplitText !== "undefined";
  var HAS_LENIS = typeof Lenis !== "undefined";
  var REDUCE    = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var IS_TOUCH  = window.matchMedia("(hover: none), (pointer: coarse)").matches;
  var DPR       = Math.min(window.devicePixelRatio || 1, 2);

  var $  = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };
  var clamp = function (v, a, b) { return v < a ? a : (v > b ? b : v); };
  var lerp  = function (a, b, t) { return a + (b - a) * t; };
  var pad2  = function (n) { n = Math.floor(n); return n < 10 ? "0" + n : "" + n; };
  var smooth = function (t) { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
  var attr = function (el, name, fallback) {
    if (!el) return fallback;
    var v = el.getAttribute(name);
    return (v === null || v === "") ? fallback : v;
  };
  var num = function (v, fallback) { var n = parseFloat(v); return isFinite(n) ? n : fallback; };

  if (HAS_GSAP && HAS_ST) { gsap.registerPlugin(ScrollTrigger); }
  if (HAS_GSAP && HAS_SPLIT) { try { gsap.registerPlugin(SplitText); } catch (e) {} }
  if (IS_TOUCH && document.body) document.body.classList.add("touch");

  /* ══════════════════════════════════════════════════════════════════════════
     PER-SECTION REGISTRY (trap H: section reloads must not leak timelines)
     ══════════════════════════════════════════════════════════════════════════ */
  var REG = {};

  function bag(key) {
    return REG[key] || (REG[key] = { st: [], tw: [], mm: [], iv: [], io: [], fn: [] });
  }
  function keep(key, kind, obj) { if (obj) bag(key)[kind].push(obj); return obj; }

  function killKey(key) {
    var b = REG[key];
    if (!b) return;
    b.st.forEach(function (s) { try { s.kill(true); } catch (e) {} });
    b.tw.forEach(function (t) {
      try { if (t.scrollTrigger) t.scrollTrigger.kill(true); t.kill(); } catch (e) {}
    });
    b.mm.forEach(function (m) { try { m.revert(); } catch (e) {} });
    b.iv.forEach(function (i) { clearInterval(i); });
    b.io.forEach(function (o) { try { o.disconnect(); } catch (e) {} });
    b.fn.forEach(function (f) { try { f(); } catch (e) {} });
    delete REG[key];
  }

  /* Cross-section element handles. Re-resolved whenever a section loads, so a
     record, a beam or a countdown target that got replaced keeps working. */
  var G = {
    lenis: null, nav: null, cur: null, beam: null, beamDot: null,
    heroSpin: null, deckSpin: null, ambient: null
  };
  function resolve() {
    G.nav      = $("#nav");
    G.cur      = $(".cur");
    G.beam     = $("#beamFill");
    G.beamDot  = $("#beamDot");
    G.heroSpin = $("#heroSpin");
    G.deckSpin = $("#deckSpin");
    G.ambient  = $("#ambient");
  }

  /* ---- inject groove rings into every record (idempotent) ---- */
  function buildGrooves(scope) {
    $$(".rec-grooves", scope).forEach(function (g) {
      if (g.childNodes.length) return;
      var NS = "http://www.w3.org/2000/svg";
      for (var r = 76; r <= 192; r += 2.9) {
        var c = document.createElementNS(NS, "circle");
        c.setAttribute("cx", "200"); c.setAttribute("cy", "200");
        c.setAttribute("r", r.toFixed(1));
        c.setAttribute("fill", "none");
        var lit = (Math.round(r) % 9 === 0);
        c.setAttribute("stroke", lit ? "#C6884E" : "#8A5F3A");
        c.setAttribute("stroke-opacity", lit ? "0.16" : "0.09");
        c.setAttribute("stroke-width", "0.7");
        g.appendChild(c);
      }
    });
  }

  /* ---- headline splitting ---- */
  function splitChars(el) {
    if (HAS_SPLIT) {
      try { var s = new SplitText(el, { type: "chars" }); return s.chars; } catch (e) {}
    }
    var txt = el.textContent, frag = document.createDocumentFragment(), out = [];
    el.textContent = "";
    for (var i = 0; i < txt.length; i++) {
      var sp = document.createElement("span");
      sp.style.display = "inline-block";
      sp.textContent = txt[i] === " " ? " " : txt[i];
      frag.appendChild(sp); out.push(sp);
    }
    el.appendChild(frag); return out;
  }
  function splitWords(el) {
    if (HAS_SPLIT) {
      try {
        var s = new SplitText(el, { type: "words", wordsClass: "w" });
        s.words.forEach(function (w) {
          var i = document.createElement("i");
          i.innerHTML = w.innerHTML; w.innerHTML = ""; w.appendChild(i);
        });
        return s.words;
      } catch (e) {}
    }
    return null;
  }

  /* ---- generic reveals, scoped to one section ----
     Everything carrying [data-rise] or .reveal starts INVISIBLE in the CSS
     (visibility:hidden / opacity:0) and is only ever made visible here. A
     section that reaches the page without a matching entry in MODULES is
     therefore permanently blank — see sweepUndecorated() below, which is the
     backstop for exactly that. */
  function decorate(root, key) {
    $$("[data-rise], .reveal", root).forEach(function (el) { el.dataset.aliaSeen = "1"; });
    if (HAS_GSAP && HAS_ST && !REDUCE) {
      $$("[data-rise]", root).forEach(function (el) {
        el.style.visibility = "visible";
        var chars = splitChars(el);
        gsap.set(chars, { yPercent: 110 });
        keep(key, "tw", gsap.to(chars, {
          yPercent: 0, duration: 1, ease: "power4.out", stagger: .026,
          scrollTrigger: { trigger: el, start: "top 88%" }
        }));
      });
      $$(".reveal", root).forEach(function (el) {
        keep(key, "tw", gsap.to(el, {
          opacity: 1, y: 0, duration: .9, ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 90%" }
        }));
      });
    } else {
      $$("[data-rise]", root).forEach(function (el) { el.style.visibility = "visible"; });
      $$(".reveal", root).forEach(function (el) { el.style.opacity = 1; el.style.transform = "none"; });
    }
  }

  /* ---- backstop: nothing may stay invisible because a module is missing ----
     The reveal CSS is opt-OUT: an element is hidden until JS shows it. That
     makes a missing MODULES entry silently blank an entire section rather than
     just skip its animation — which is exactly what happened when the
     corporate, lanes and cta-band sections were added without registering
     them. Anything decorate() did not claim is forced visible here, so the
     worst case is a section that appears without animating, never one that
     does not appear at all. Also covers a merchant adding a section in the
     theme editor that this bundle has never heard of. */
  function sweepUndecorated(scope) {
    $$("[data-rise]:not([data-alia-seen]), .reveal:not([data-alia-seen])", scope).forEach(function (el) {
      el.dataset.aliaSeen = "1";
      el.style.visibility = "visible";
      el.style.opacity = 1;
      el.style.transform = "none";
      if (window.console && console.warn) {
        console.warn("[10-vinyl] revealed an element no section module claimed:", el);
      }
    });
  }

  /* ---- groove divider playhead (one per divider) ---- */
  function dividerPlayhead(sel, key) {
    var b = $(sel);
    if (!b || !HAS_GSAP || !HAS_ST || REDUCE) return;
    keep(key, "tw", gsap.fromTo(b, { left: "0%" }, {
      left: "100%", ease: "none",
      scrollTrigger: { trigger: b.parentNode, start: "top bottom", end: "bottom top", scrub: true }
    }));
  }

  /* ══════════════════════════════════════════════════════════════════════════
     PRELOADER — trap E: runs exactly once, per page load, never on reload
     ══════════════════════════════════════════════════════════════════════════ */
  var BOOTED = false;

  function runPreloader(done) {
    var pre = $("#pre");
    if (!pre) { done(); return; }
    var stateEl = $("#preState");
    var spin = $("#preSpin");
    var finish = function () {
      pre.classList.add("done");
      setTimeout(function () { if (pre && pre.parentNode) pre.parentNode.removeChild(pre); done(); }, 720);
    };
    if (!HAS_GSAP || REDUCE) {
      $$(".pre-word span").forEach(function (s) { s.style.opacity = 1; s.style.transform = "none"; });
      setTimeout(finish, REDUCE ? 200 : 600);
      return;
    }
    var sideWord = attr(stateEl, "data-side", "SIDE A");
    var spinWord = attr(stateEl, "data-spin", "SPIN");
    var tl = gsap.timeline({ onComplete: finish });
    if (spin) tl.fromTo(spin, { rotation: -40 }, { rotation: 320, duration: 2.0, ease: "power2.out", transformOrigin: "66px 66px" }, 0);
    tl.to("#preWord span", { opacity: 1, y: 0, duration: .55, ease: "power3.out", stagger: .09 }, .2);
    if (stateEl) {
      tl.call(function () { stateEl.textContent = sideWord; }, null, .8);
      tl.call(function () { stateEl.textContent = spinWord; }, null, 1.6);
    }
    // total ~2.1s, under the 2.5s cap
  }

  /* If the editor re-injects the chrome section after boot, the preloader comes
     back as a full-screen overlay. Dismiss it on sight. */
  function dismissPreloader() {
    if (!BOOTED) return;
    var pre = $("#pre");
    if (pre && pre.parentNode) pre.parentNode.removeChild(pre);
    var dock = $(".alia-cta-dock");
    if (dock) dock.classList.add("revealed");
  }

  /* ══════════════════════════════════════════════════════════════════════════
     GLOBALS — created once, never torn down, targets re-resolved on reload
     ══════════════════════════════════════════════════════════════════════════ */
  var spinState = { hero: 0, deck: 0, heroBoost: 0, deckActive: false, heroActive: false };
  function setSpin(el, deg) { if (el) el.style.transform = "rotate(" + deg + "deg)"; }

  function initGlobals() {
    /* ---- Lenis + ScrollTrigger sync: exactly one smooth-scroll instance ---- */
    if (HAS_LENIS && HAS_GSAP && HAS_ST && !REDUCE && !G.lenis) {
      G.lenis = new Lenis({
        duration: 1.1,
        easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true
      });
      G.lenis.on("scroll", ScrollTrigger.update);
      gsap.ticker.add(function (t) { G.lenis.raf(t * 1000); });
      gsap.ticker.lagSmoothing(0);
    }

    /* ---- Cursor (tiny stylus). Delegated, so sections can come and go. ---- */
    if (!IS_TOUCH) {
      var cx = window.innerWidth / 2, cy = window.innerHeight / 2, tx = cx, ty = cy;
      window.addEventListener("mousemove", function (e) { tx = e.clientX; ty = e.clientY; }, { passive: true });
      (function loop() {
        cx = lerp(cx, tx, .2); cy = lerp(cy, ty, .2);
        if (G.cur) G.cur.style.transform = "translate(" + cx + "px," + cy + "px)";
        requestAnimationFrame(loop);
      })();
      var HOT = "a,button,.plate,.floor-card,.zone,.faq-q,.late-row";
      var hot = function (t) {
        if (!t) return null;
        if (t.closest) return t.closest(HOT);
        while (t && t.nodeType === 1) { if (t.matches && t.matches(HOT)) return t; t = t.parentNode; }
        return null;
      };
      document.addEventListener("mouseover", function (e) { if (G.cur && hot(e.target)) G.cur.classList.add("on"); }, { passive: true });
      document.addEventListener("mouseout",  function (e) { if (G.cur && hot(e.target)) G.cur.classList.remove("on"); }, { passive: true });
    }

    /* ---- Nav shrink ---- */
    var onNav = function () { if (G.nav) G.nav.classList.toggle("shrunk", window.scrollY > 60); };
    onNav();
    window.addEventListener("scroll", onNav, { passive: true });

    /* ---- Scroll progress groove ---- */
    function setBeam(p) {
      if (G.beam) G.beam.style.transform = "scaleX(" + p + ")";
      if (G.beamDot) G.beamDot.style.transform = "translateX(" + (p * window.innerWidth) + "px)";
    }
    if (HAS_GSAP && HAS_ST) {
      gsap.to({ v: 0 }, {
        v: 1, ease: "none",
        scrollTrigger: {
          trigger: document.documentElement, start: "top top", end: "bottom bottom", scrub: true,
          onUpdate: function (s) { setBeam(s.progress); }
        }
      });
    } else {
      window.addEventListener("scroll", function () {
        var h = document.documentElement.scrollHeight - window.innerHeight;
        setBeam(h > 0 ? window.scrollY / h : 0);
      }, { passive: true });
    }

    /* ════════ RECORD SPIN ENGINE (shared by hero + deck) ════════ */
    (function spinLoop() {
      if (!REDUCE) {
        spinState.hero += 0.28 + spinState.heroBoost;
        spinState.deck += 0.5;
        spinState.heroBoost *= 0.92;
        if (spinState.heroActive) setSpin(G.heroSpin, spinState.hero);
        if (spinState.deckActive) setSpin(G.deckSpin, spinState.deck);
      }
      requestAnimationFrame(spinLoop);
    })();
    if (!("IntersectionObserver" in window)) { spinState.heroActive = true; spinState.deckActive = true; }

    /* ════════ COUNTDOWN (doors + last call) ════════
       Feeds the hero readouts AND the Last Call clock from one timer. The
       schedule is read off the Last Call blocks, with the confirmed
       Thu/Fri/Sat 7PM hours as the fallback when that section is absent. */
    var FALLBACK = [{ dow: 4, open: 19, len: 6 }, { dow: 5, open: 19, len: 7 }, { dow: 6, open: 19, len: 7 }];
    function schedule() {
      var out = [];
      $$(".le-hours span[data-dow]").forEach(function (s) {
        var d = num(s.getAttribute("data-dow"), -1);
        if (d < 0 || d > 6) return;
        out.push({ dow: d, open: num(s.getAttribute("data-open"), 19), len: num(s.getAttribute("data-len"), 6) });
      });
      return out.length ? out : FALLBACK;
    }
    function windows(from) {
      var plan = schedule(), list = [];
      for (var d = 0; d < 9; d++) {
        var day = new Date(from.getFullYear(), from.getMonth(), from.getDate() + d, 0, 0, 0, 0);
        var dow = day.getDay();
        for (var i = 0; i < plan.length; i++) {
          if (plan[i].dow === dow) {
            var open = new Date(day); open.setHours(plan[i].open, 0, 0, 0);
            var close = new Date(open); close.setHours(open.getHours() + plan[i].len);
            list.push({ open: open, close: close });
          }
        }
      }
      return list;
    }
    function state(now) {
      var wins = windows(new Date(now.getTime() - 24 * 3600 * 1000));
      for (var i = 0; i < wins.length; i++) {
        if (now >= wins[i].open && now < wins[i].close) return { open: true, target: wins[i].close };
      }
      var future = windows(now);
      for (var j = 0; j < future.length; j++) {
        if (future[j].open > now) return { open: false, target: future[j].open };
      }
      return { open: false, target: future.length ? future[0].open : now };
    }
    function tick() {
      var leH = $("#leH"), leM = $("#leM"), leS = $("#leS"), leState = $("#leState");
      var heroDoors = $("#heroDoors"), heroOpen = $("#heroOpen"), heroDoorsK = $("#heroDoorsK");
      var now = new Date();
      var st = state(now);
      var diff = Math.max(0, (st.target - now) / 1000);
      var h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), sec = Math.floor(diff % 60);
      if (leH && leM && leS) { leH.textContent = pad2(h); leM.textContent = pad2(m); leS.textContent = pad2(sec); }
      if (leState) {
        leState.textContent = st.open
          ? attr(leState, "data-open", "ON THE FLOOR NOW · LAST CALL COUNTDOWN")
          : attr(leState, "data-closed", "DOORS OPEN IN · SEE YOU AT SEVEN");
      }
      if (heroDoors) heroDoors.textContent = pad2(h) + ":" + pad2(m) + ":" + pad2(sec);
      if (heroDoorsK) {
        heroDoorsK.textContent = st.open
          ? attr(heroDoorsK, "data-open", "Last Call In")
          : attr(heroDoorsK, "data-closed", "Doors In");
      }
      if (heroOpen) {
        heroOpen.textContent = st.open
          ? attr(heroOpen, "data-open", "OPEN NOW")
          : attr(heroOpen, "data-closed", "CLOSED");
      }
    }
    tick();
    setInterval(tick, 1000);

    /* ════════ IMAGE LIGHTBOX ════════
       Delegated on `document`, so it covers the corporate gallery, article
       bodies and anything a merchant adds later, and survives section reloads
       with nothing to rebind. Blog card thumbnails are excluded on purpose:
       they live inside an <a> and must navigate to the article.

       data-lenis-prevent on the overlay — Lenis sees the retargeted element,
       so without it the page scrolls behind the open image on touch. */
    var LB_SEL = '.corp-shot img, .ng-body img, .ng-hero-img';
    var lb = null, lbImg = null, lbCap = null, lastFocus = null;

    function buildLb() {
      if (lb) return;
      lb = document.createElement('div');
      lb.className = 'alia-lb';
      lb.setAttribute('data-lenis-prevent', '');
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', 'Image preview');
      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'alia-lb-close';
      close.setAttribute('aria-label', 'Close preview');
      close.innerHTML = '&times;';
      lbImg = document.createElement('img');
      lbImg.alt = '';
      lbCap = document.createElement('p');
      lbCap.className = 'alia-lb-cap';
      lb.appendChild(close); lb.appendChild(lbImg); lb.appendChild(lbCap);
      document.body.appendChild(lb);
      close.addEventListener('click', closeLb);
      /* click anywhere off the image closes; clicks on the image itself do not */
      lb.addEventListener('click', function (e) { if (e.target !== lbImg) closeLb(); });
    }

    function openLb(img) {
      buildLb();
      /* prefer the biggest source the browser already knows about */
      lbImg.src = img.currentSrc || img.src;
      lbImg.alt = img.alt || '';
      var fig = img.closest ? img.closest('figure') : null;
      var cap = fig && fig.querySelector('figcaption');
      var text = (cap && cap.textContent.trim()) || img.alt || '';
      lbCap.textContent = text;
      lbCap.hidden = !text;
      lastFocus = document.activeElement;
      lb.classList.add('open');
      document.body.classList.add('alia-lb-open');
      if (G.lenis && G.lenis.stop) G.lenis.stop();
      lb.querySelector('.alia-lb-close').focus();
    }

    function closeLb() {
      if (!lb || !lb.classList.contains('open')) return;
      lb.classList.remove('open');
      document.body.classList.remove('alia-lb-open');
      if (G.lenis && G.lenis.start) G.lenis.start();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || t.nodeType !== 1 || t.tagName !== 'IMG') return;
      if (!t.matches || !t.matches(LB_SEL)) return;
      if (t.closest && t.closest('a')) return;   /* let links navigate */
      e.preventDefault();
      openLb(t);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeLb(); });

    /* ---- Easter egg: double-click drops a fresh needle ---- */
    document.addEventListener("dblclick", function () {
      if (!HAS_GSAP || REDUCE) return;
      spinState.heroBoost = 2.4;
      spinState.deck += 40;
      var flash = document.createElement("div");
      flash.style.cssText = "position:fixed;inset:0;z-index:9995;pointer-events:none;background:radial-gradient(60vw 60vw at 50% 50%,rgba(224,168,103,.26),transparent 60%);opacity:0";
      document.body.appendChild(flash);
      gsap.to(flash, {
        opacity: 1, duration: .14, ease: "power2.out", onComplete: function () {
          gsap.to(flash, { opacity: 0, duration: .5, ease: "power2.in", onComplete: function () { flash.remove(); } });
        }
      });
    });

    /* ---- fonts ready + load refresh ---- */
    if (HAS_ST && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
    }
    if (HAS_ST) {
      window.addEventListener("load", function () { ScrollTrigger.refresh(); });
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     SECTION MODULES
     ══════════════════════════════════════════════════════════════════════════ */

  /* ---------- CHROME: grain, ambient, cursor, beam, preloader ---------- */
  function M_chrome(root, key) {
    buildGrooves(document);
    dismissPreloader();
  }

  /* ---------- NAV + MEGA MENU ---------- */
  function M_nav(root, key) {
    if (G.nav) G.nav.classList.toggle("shrunk", window.scrollY > 60);

    var btn = $("#megaBtn", root);
    var panel = $("#megaPanel", root);
    if (!btn || !panel) return;

    /* Animating to a measured pixel height rather than `auto` so the panel can
       transition at all, then releasing it to auto once open so a merchant
       adding a column in the editor does not get a clipped panel. */
    var open = false;
    function setOpen(next) {
      if (next === open) return;
      open = next;
      btn.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) {
        panel.hidden = false;
        /* The panel hangs off a position:fixed bar, so on a phone its content
           is easily taller than what is left of the viewport — and with
           overflow:hidden there was no way to reach the bottom of it. Cap it
           to the space under the bar and let it scroll.

           Measured on open rather than in CSS because .nav changes height when
           it shrinks past 60px of scroll. */
        var navH = G.nav ? Math.round(G.nav.getBoundingClientRect().height) : 84;
        document.documentElement.style.setProperty("--nav-h", navH + "px");
        panel.classList.add("open");
        /* animate to the CAPPED height, not the full content height, or the
           panel visually stops growing a third of the way through */
        var room = window.innerHeight - navH;
        panel.style.height = Math.min(panel.scrollHeight, room) + "px";
        clearTimeout(panel._t);
        panel._t = setTimeout(function () {
          if (!open) return;
          panel.style.height = "auto";
          /* only now is it safe to scroll inside: during the transition an
             overflow of auto would fight the height animation */
          panel.classList.add("settled");
        }, 440);
      } else {
        clearTimeout(panel._t);
        panel.classList.remove("settled");
        /* the RENDERED height, which may be the cap rather than scrollHeight —
           collapsing from the full content height would jump first */
        panel.style.height = Math.round(panel.getBoundingClientRect().height) + "px";
        void panel.offsetHeight; /* commit the pixel height before collapsing */
        panel.classList.remove("open");
        panel.style.height = "0px";
        panel._t = setTimeout(function () { if (!open) panel.hidden = true; }, 440);
      }
    }

    btn.addEventListener("click", function (e) { e.stopPropagation(); setOpen(!open); });
    /* a link inside the panel navigates; close so it is not left hanging open
       when the target is an in-page anchor on this same page */
    $$("a", panel).forEach(function (a) { a.addEventListener("click", function () { setOpen(false); }); });
    panel.addEventListener("click", function (e) { e.stopPropagation(); });

    var onDoc = function () { setOpen(false); };
    var onKey = function (e) { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    keep(key, "fn", function () {
      clearTimeout(panel._t);
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    });
  }

  /* ---------- HERO ---------- */
  function M_hero(root, key) {
    buildGrooves(root);
    decorate(root, key);

    var rpmEl = $("#heroRpm");
    if (rpmEl) {
      var unit = attr(rpmEl, "data-unit", "RPM");
      var tt = 0;
      keep(key, "iv", setInterval(function () {
        tt += 0.5;
        rpmEl.innerHTML = (33.3 + Math.sin(tt) * 0.15).toFixed(1) + ' <small>' + unit + '</small>';
      }, 900));
    }

    if (HAS_GSAP && !REDUCE) {
      var ht = $("#heroTitle");
      var hchars = [];
      if (ht) { hchars = splitChars(ht); hchars.forEach(function (c) { c.classList.add("ch"); }); }
      var tl = gsap.timeline({ delay: .1 });
      tl.from(hchars, { yPercent: 118, opacity: 0, duration: 1.1, ease: "power4.out", stagger: .07 }, 0);
      tl.to(".hero-eye", { opacity: 1, duration: .6, ease: "power2.out" }, .2);
      tl.fromTo("#heroRec", { opacity: 0, scale: .86, rotation: -12 }, { opacity: 1, scale: 1, rotation: 0, duration: 1.2, ease: "power3.out" }, .3);
      tl.to("#heroSub", { opacity: 1, y: 0, duration: .8, ease: "power3.out" }, .6);
      tl.to("#heroFoot", { opacity: 1, y: 0, duration: .8, ease: "power3.out" }, .8);
      keep(key, "tw", tl);
    } else {
      [".hero-eye", "#heroSub", "#heroFoot", "#heroRec"].forEach(function (s) { var e = $(s); if (e) e.style.opacity = 1; });
    }

    if (REDUCE) setSpin(G.heroSpin, 8);

    /* IO gate on the hero record */
    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (en) {
        en.forEach(function (e) { spinState.heroActive = e.isIntersecting; });
      }, { threshold: .05 });
      var hr = $("#heroRec");
      if (hr) { io.observe(hr); keep(key, "io", io); }
    } else {
      spinState.heroActive = true;
    }

    /* hero record reacts to scroll velocity (a light boost) */
    if (HAS_GSAP && HAS_ST && !REDUCE) {
      keep(key, "st", ScrollTrigger.create({
        trigger: "#hero", start: "top top", end: "bottom top", scrub: true,
        onUpdate: function (s) { spinState.heroBoost = Math.min(2.4, Math.abs(s.getVelocity() / 1600)); }
      }));
    }
  }

  /* ---------- MANIFESTO ---------- */
  function M_manifesto(root, key) {
    decorate(root, key);
    dividerPlayhead("#grDiv1", key);
  }

  /* ---------- THE DECK (needle journey + flip) ---------- */
  var DEFAULT_TRACKS = [
    { side: "SIDE A · DINNER", name: "DOORS",           time: "19:00", len: "01:28", sub: "Seven o'clock at 997 Saint-Jacques. The needle drops. Bone light, a low warmth, the first pour of the night." },
    { side: "SIDE A · DINNER", name: "FIRST PLATES",    time: "20:30", len: "01:52", sub: "Smoked feta and charred lemon land on the table. The room settles into its groove." },
    { side: "SIDE A · DINNER", name: "THE TABLE FILLS", time: "21:30", len: "02:04", sub: "Chairs pull in, the pace lifts, the pours keep coming. Side A plays out to the last plate." },
    { side: "SIDE B · THE FLOOR", name: "THE TURN",     time: "23:00", len: "01:40", sub: "The record flips. Lights drop a stop, the palette deepens, and dinner learns it is a floor." },
    { side: "SIDE B · THE FLOOR", name: "THE FLOOR",    time: "00:30", len: "02:20", sub: "Dense and full, the room on the one, the low end carrying the night into the small hours." },
    { side: "SIDE B · THE FLOOR", name: "LAST CALL",    time: "01:45", len: "01:15", sub: "The embers go low. The last groove runs out onto Saint-Jacques and the platter slows to a stop." }
  ];

  function M_deck(root, key) {
    buildGrooves(root);

    var TRACKS = DEFAULT_TRACKS;
    var payload = $("#deckTracks", root) || $("#deckTracks");
    if (payload) {
      try {
        var parsed = JSON.parse(payload.textContent);
        if (parsed && parsed.length) TRACKS = parsed;
      } catch (e) {}
    }
    var TOTAL = TRACKS.length;
    var HALF = Math.max(1, Math.ceil(TOTAL / 2));
    var BCOUNT = Math.max(1, TOTAL - HALF);

    var FA = clamp(num(root.getAttribute("data-flip-start"), 42) / 100, 0.05, 0.9);
    var FB = clamp(num(root.getAttribute("data-flip-end"), 58) / 100, FA + 0.02, 0.95);

    var sideEl = $("#deckSide"), tnoEl = $("#deckTno"), trackEl = $("#deckTrack"), subEl = $("#deckSub");
    var timeEl = $("#deckTime"), lenEl = $("#deckLen"), fillEl = $("#deckFill");
    var labelSide = $("#deckLabelSide"), labelDisc = $("#deckLabelDisc");
    var recWrap = $("#recWrap"), armG = $("#armG"), deckBg = $("#deckBg");
    var listEl = $("#deckList");
    var SIDE_A = attr(labelSide, "data-side-a", "SIDE A");
    var SIDE_B = attr(labelSide, "data-side-b", "SIDE B");

    /* build the tracklist sidebar */
    if (listEl) {
      listEl.innerHTML = "";
      TRACKS.forEach(function (t, i) {
        var row = document.createElement("div");
        row.className = "tk";
        row.setAttribute("data-tk", i);
        row.innerHTML = '<span class="n">' + (i < HALF ? "A" : "B") + ((i % HALF) + 1) + '</span>' +
                        '<span class="nm">' + t.name + '</span>' +
                        '<span class="tm">' + t.len + '</span>';
        listEl.appendChild(row);
      });
    }
    var rows = $$(".tk", listEl);
    var lastIdx = -1, lastSideB = null;

    function setArm(deg) {
      if (!armG) return;
      if (HAS_GSAP) gsap.set(armG, { rotation: deg, svgOrigin: "188 42" });
      else armG.setAttribute("transform", "rotate(" + deg + " 188 42)");
    }
    function deepen(k) {
      // k: 0 (Side A roast) .. 1 (Side B deep). interpolate within brand tokens only.
      var a = [36, 26, 17], b = [21, 13, 7]; // #241A11 -> #150D07
      var r = Math.round(lerp(a[0], b[0], k)), g = Math.round(lerp(a[1], b[1], k)), bl = Math.round(lerp(a[2], b[2], k));
      var col = "rgb(" + r + "," + g + "," + bl + ")";
      if (deckBg) deckBg.style.background = "radial-gradient(60vw 60vw at 50% 46%, rgba(138,95,58," + (0.10 - 0.03 * k).toFixed(3) + "), transparent 66%), " + col;
      if (G.ambient) G.ambient.style.opacity = (1 - 0.35 * k).toFixed(2);
    }
    function apply(p) {
      p = clamp(p, 0, 1);
      var idx, flipY = 0, lift = 0, scl = 1, sideB = false, trackLocal = 0, armDeg;
      if (p < FA) {
        var la = p / FA;                        // 0..1 across side A
        idx = clamp(Math.floor(la * HALF), 0, HALF - 1);
        trackLocal = (la * HALF) - idx;
        armDeg = lerp(-20, -4, la);
        sideB = false; flipY = 0;
      } else if (p <= FB) {
        var fp = (p - FA) / (FB - FA);          // 0..1 flip
        var e = smooth(fp);
        flipY = e * 180;
        lift = -Math.sin(fp * Math.PI) * 7;
        scl = 1 + Math.sin(fp * Math.PI) * 0.06;
        armDeg = lerp(-4, 10, Math.sin(fp * Math.PI)); // arm lifts away and returns
        sideB = fp >= 0.5;
        idx = fp < 0.5 ? HALF - 1 : Math.min(HALF, TOTAL - 1);
        trackLocal = fp < 0.5 ? 1 : 0;
      } else {
        var lb = (p - FB) / (1 - FB);           // 0..1 across side B
        idx = clamp(HALF + Math.floor(lb * BCOUNT), HALF, TOTAL - 1);
        trackLocal = (lb * BCOUNT) - (idx - HALF);
        armDeg = lerp(-20, -4, lb);
        sideB = true; flipY = 180;
      }
      // apply transforms
      if (recWrap) recWrap.style.transform = "translateY(" + lift + "%) scale(" + scl.toFixed(3) + ") rotateY(" + flipY + "deg)";
      setArm(armDeg);
      var deepK = clamp((p - FA) / (FB - FA), 0, 1);
      deepen(deepK);
      // label side swap at edge-on
      if (sideB !== lastSideB) {
        lastSideB = sideB;
        if (labelSide) labelSide.textContent = sideB ? SIDE_B : SIDE_A;
        if (labelDisc) labelDisc.setAttribute("fill", sideB ? "url(#labelGradB)" : "url(#labelGrad)");
      }
      // track text swap
      if (idx !== lastIdx) {
        lastIdx = idx;
        var t = TRACKS[idx];
        if (t) {
          if (sideEl) sideEl.textContent = t.side;
          if (tnoEl) tnoEl.textContent = "TRACK " + pad2(idx + 1) + " / " + pad2(TOTAL);
          if (trackEl) trackEl.textContent = t.name;
          if (subEl) subEl.textContent = t.sub;
          if (timeEl) timeEl.textContent = t.time;
          if (lenEl) lenEl.textContent = t.len;
          rows.forEach(function (r, ri) { r.classList.toggle("on", ri === idx); });
        }
      }
      if (fillEl) fillEl.style.width = (clamp(trackLocal, 0, 1) * 100).toFixed(1) + "%";
    }

    // initial state
    apply(0);

    /* IO gate on the deck record */
    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (en) {
        en.forEach(function (e) { spinState.deckActive = e.isIntersecting; });
      }, { threshold: .02 });
      var ds = $("#deckStage");
      if (ds) { io.observe(ds); keep(key, "io", io); }
    } else {
      spinState.deckActive = true;
    }

    if (REDUCE) {
      setSpin(G.deckSpin, 8);
      // show a settled Side B still
      if (recWrap) recWrap.style.transform = "rotateY(180deg)";
      apply(Math.min(0.99, FB + 0.04));
      return;
    }

    if (HAS_GSAP && HAS_ST) {
      var mm = gsap.matchMedia();
      keep(key, "mm", mm);
      mm.add("(min-width:881px)", function () {
        var st = ScrollTrigger.create({
          trigger: "#deck", start: "top top", end: "bottom bottom",
          pin: "#deckPin", pinSpacing: true, anticipatePin: 1, scrub: .6,
          onUpdate: function (self) { apply(self.progress); }
        });
        return function () { st.kill(true); };
      });
      mm.add("(max-width:880px)", function () {
        var st = ScrollTrigger.create({
          trigger: "#deck", start: "top top", end: "bottom bottom",
          pin: "#deckPin", pinSpacing: true, anticipatePin: 1, scrub: .6,
          onUpdate: function (self) { apply(self.progress); }
        });
        return function () { st.kill(true); };
      });
    }
  }

  /* ---------- THE GROOVE CANVAS ---------- */
  function M_groove(root, key) {
    decorate(root, key);

    var cv = $("#gvCanvas", root), stage = $("#gvStage", root);
    if (!cv || !stage) return;
    var ctx = cv.getContext("2d");
    var W = 0, H = 0, running = false, raf = 0, t0 = performance.now();
    var prog = 0;
    var rpmEl = $("#gvRpm", root), depthEl = $("#gvDepth", root), resEl = $("#gvRes", root);
    var uRpm = attr(rpmEl, "data-unit", "RPM");
    var uDepth = attr(depthEl, "data-unit", "\u00b5m");
    var uRes = attr(resEl, "data-unit", "dB");
    var uiT = 0;

    function size() {
      var r = stage.getBoundingClientRect();
      cv.width = Math.max(1, Math.round(r.width * DPR));
      cv.height = Math.max(1, Math.round(r.height * DPR));
      W = cv.width; H = cv.height;
    }
    size();

    function draw(now) {
      var t = (now - t0) / 1000;
      ctx.clearRect(0, 0, W, H);
      var cxp = W * 0.5, cyp = H * 0.5;
      var maxR = Math.min(W, H) * 0.46;
      var labelR = maxR * 0.28;
      var swell = 0.4 + prog * 0.9;
      // concentric grooves
      var rings = 46;
      for (var i = 0; i < rings; i++) {
        var fr = i / rings;
        var rr = labelR + (maxR - labelR) * fr;
        // a travelling read-wave moving inward
        var phase = Math.sin(fr * 22 - t * 2.2) * 0.5 + 0.5;
        var reso = Math.sin(fr * 7 - t * 1.1) * 0.5 + 0.5;
        var a = 0.05 + phase * 0.14 * swell + reso * 0.05;
        ctx.beginPath();
        ctx.arc(cxp, cyp, rr, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(" + (138 + Math.round(phase * 86)) + "," + (95 + Math.round(phase * 73)) + "," + (58 + Math.round(phase * 45)) + "," + a.toFixed(3) + ")";
        ctx.lineWidth = (0.6 + phase * 1.4 * swell) * DPR;
        ctx.stroke();
      }
      // the needle read point travelling inward
      var readFr = (t * 0.12) % 1;
      var readR = maxR - (maxR - labelR) * readFr;
      var ang = t * 3.4;
      var rx = cxp + Math.cos(ang) * readR, ry = cyp + Math.sin(ang) * readR;
      ctx.beginPath(); ctx.arc(rx, ry, 3.4 * DPR, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(224,168,103,0.9)"; ctx.fill();
      ctx.beginPath(); ctx.arc(rx, ry, 9 * DPR, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(224,168,103,0.18)"; ctx.fill();
      // label
      ctx.beginPath(); ctx.arc(cxp, cyp, labelR, 0, Math.PI * 2);
      var lg = ctx.createRadialGradient(cxp - labelR * 0.3, cyp - labelR * 0.3, labelR * 0.1, cxp, cyp, labelR);
      lg.addColorStop(0, "#C6884E"); lg.addColorStop(1, "#8A5F3A");
      ctx.fillStyle = lg; ctx.fill();
      ctx.beginPath(); ctx.arc(cxp, cyp, 3 * DPR, 0, Math.PI * 2); ctx.fillStyle = "#150D07"; ctx.fill();
      // readouts
      uiT++;
      if (uiT % 6 === 0) {
        if (rpmEl) rpmEl.innerHTML = (33.3 + Math.sin(t * 0.7) * 0.2).toFixed(1) + '<small>' + uRpm + '</small>';
        if (depthEl) depthEl.innerHTML = Math.round(58 + swell * 30 + Math.sin(t * 1.3) * 3) + '<small>' + uDepth + '</small>';
        if (resEl) resEl.innerHTML = (-24 + swell * 14).toFixed(1) + '<small>' + uRes + '</small>';
      }
      if (running) raf = requestAnimationFrame(draw);
    }
    function start() { if (running) return; running = true; t0 = performance.now(); raf = requestAnimationFrame(draw); }
    function stop() { running = false; if (raf) cancelAnimationFrame(raf); }

    var onResize = function () { size(); if (REDUCE) draw(performance.now()); };
    window.addEventListener("resize", onResize);
    keep(key, "fn", function () { stop(); window.removeEventListener("resize", onResize); });

    if (REDUCE) { prog = 0.5; draw(performance.now()); }
    else if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (en) {
        en.forEach(function (e) { e.isIntersecting ? start() : stop(); });
      }, { threshold: .05 });
      io.observe(cv);
      keep(key, "io", io);
    } else start();

    if (HAS_GSAP && HAS_ST && !REDUCE) {
      keep(key, "st", ScrollTrigger.create({
        trigger: "#groove", start: "top 80%", end: "bottom 30%", scrub: .6,
        onUpdate: function (s) { prog = s.progress; }
      }));
    }
  }

  /* ---------- INTERLUDE ---------- */
  function M_interlude(root, key) {
    decorate(root, key);
    if (HAS_GSAP && HAS_ST && !REDUCE) {
      $$("[data-quote]", root).forEach(function (el) {
        var words = splitWords(el);
        if (!words) return;
        var inner = words.map(function (w) { return w.querySelector("i"); });
        gsap.set(inner, { yPercent: 120 });
        keep(key, "tw", gsap.to(inner, {
          yPercent: 0, duration: 1.1, ease: "power4.out", stagger: .05,
          scrollTrigger: { trigger: el, start: "top 80%" }
        }));
      });
    }
    dividerPlayhead("#grDiv2", key);
  }

  /* ---------- THE FLOOR ---------- */
  function M_floor(root, key) {
    decorate(root, key);

    var zones = $$(".zone", root);
    var seatsEl = $("#floorSeats");
    zones.forEach(function (z) {
      z.addEventListener("mouseenter", function () { z.classList.add("act"); });
      z.addEventListener("mouseleave", function () { z.classList.remove("act"); });
      z.addEventListener("click", function () {
        zones.forEach(function (o) { o.classList.remove("act"); });
        z.classList.add("act");
        var el = $("#floorSeats");
        if (!el) return;
        var seats = attr(z, "data-seats", "");
        el.textContent = seats ? ("SEATS " + seats + " OPEN") : attr(z, "data-label", "");
      });
    });

    /* live seat drift — PLACEHOLDER atmosphere, not a real availability feed */
    if (seatsEl) {
      var base = num(root.getAttribute("data-seat-base"), 24);
      var total = num(root.getAttribute("data-seat-total"), 42);
      var swing = num(root.getAttribute("data-seat-swing"), 3);
      var re = new RegExp("OF\\s+" + total + "\\b");
      var tt = 0;
      keep(key, "iv", setInterval(function () {
        var el = $("#floorSeats");
        if (!el || !re.test(el.textContent)) return;
        tt++;
        var v = base + Math.round(Math.sin(tt * 0.5) * swing);
        el.textContent = "SEATS " + v + " OF " + total + " OPEN";
      }, 4000));
    }
  }

  /* ---------- RESERVE BAND ---------- */
  function M_reserve(root, key) {
    decorate(root, key);
    if (HAS_GSAP && HAS_ST && !REDUCE) {
      var rz = $("#rzTitle", root);
      if (rz) {
        var rc = splitChars(rz);
        gsap.set(rc, { yPercent: 120, opacity: 0 });
        keep(key, "tw", gsap.to(rc, {
          yPercent: 0, opacity: 1, duration: 1.1, ease: "power4.out", stagger: .035,
          scrollTrigger: { trigger: rz, start: "top 82%" }
        }));
      }
    }
  }

  /* ---------- FAQ ---------- */
  function M_faq(root, key) {
    decorate(root, key);
    $$(".faq-item", root).forEach(function (item) {
      var q = item.querySelector(".faq-q"), a = item.querySelector(".faq-a");
      if (!q || !a) return;
      q.addEventListener("click", function () {
        var open = item.classList.contains("open");
        $$(".faq-item.open").forEach(function (o) {
          if (o !== item) {
            o.classList.remove("open");
            var oa = o.querySelector(".faq-a");
            if (!oa) return;
            if (HAS_GSAP && !REDUCE) gsap.to(oa, { height: 0, duration: .4, ease: "power2.inOut" });
            else oa.style.height = "0px";
          }
        });
        if (open) {
          item.classList.remove("open");
          if (HAS_GSAP && !REDUCE) gsap.to(a, { height: 0, duration: .4, ease: "power2.inOut" });
          else a.style.height = "0px";
        } else {
          item.classList.add("open");
          var target = a.scrollHeight;
          if (HAS_GSAP && !REDUCE) gsap.fromTo(a, { height: 0 }, { height: target, duration: .5, ease: "power3.out", onComplete: function () { a.style.height = "auto"; } });
          else a.style.height = "auto";
        }
      });
    });
  }

  /* ---------- FOOTER MARQUEE ---------- */
  function M_footer(root, key) {
    decorate(root, key);
    var track = $("#footMarq .track", root) || $("#footMarq .track");
    if (!track || !HAS_GSAP || REDUCE) return;
    var w = track.scrollWidth / 2;
    if (!w) return;
    keep(key, "tw", gsap.to(track, {
      x: -w, duration: 26, ease: "none", repeat: -1,
      modifiers: { x: function (x) { return (parseFloat(x) % w) + "px"; } }
    }));
  }

  /* ---------- STICKY CTA DOCK ---------- */
  function M_dock(root, key) {
    var d = $(".alia-cta-dock");
    if (!d) return;
    function reveal() { d.classList.add("revealed"); }
    if (BOOTED) { reveal(); return; }
    var SEL = '#preloader,#intro,#introStatement,[id*="preload" i],[class*="preloader" i],[class*="intro-idx"],[class*="intro-col"],[class*="intro-grid"],.intro-statement,.d-veil,.hero-veil,.tex-veil,.pre-veil,[class*="veil"],.pre';
    var pre = document.querySelector(SEL);
    if (pre) {
      var start = Date.now();
      var iv = setInterval(function () {
        var el = document.querySelector(SEL), gone = !el;
        if (el) {
          var cs = getComputedStyle(el);
          gone = (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.1);
        }
        if (gone || (Date.now() - start) > 6000) { reveal(); clearInterval(iv); }
      }, 200);
      keep(key, "iv", iv);
    } else {
      if (document.readyState === 'complete') { setTimeout(reveal, 700); }
      else { window.addEventListener('load', function () { setTimeout(reveal, 700); }); }
    }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     MODULE TABLE + SHOPIFY SECTION LIFECYCLE
     ══════════════════════════════════════════════════════════════════════════ */
  var MODULES = [
    { name: "chrome",    sel: ".grain, .ambient, .cur, .beam, #pre", init: M_chrome },
    { name: "nav",       sel: "#nav",           init: M_nav },
    { name: "hero",      sel: "#hero",          init: M_hero },
    { name: "manifesto", sel: "#manifesto",     init: M_manifesto },
    { name: "deck",      sel: "#deck",          init: M_deck },
    { name: "corporate", sel: "#corporate",     init: decorate },
    { name: "sidea",     sel: "#sidea",         init: decorate },
    { name: "groove",    sel: "#groove",        init: M_groove },
    { name: "interlude", sel: "#interlude",     init: M_interlude },
    { name: "lanes",     sel: "#lanes",         init: decorate },
    { name: "floor",     sel: "#floor",         init: M_floor },
    { name: "sideb",     sel: "#sideb",         init: decorate },
    { name: "lastcall",  sel: "#lastcall",      init: decorate },
    { name: "ctaband",   sel: "#cta-band",      init: decorate },
    { name: "reserve",   sel: "#reserve",       init: M_reserve },
    { name: "faq",       sel: "#faq",           init: M_faq },
    { name: "footer",    sel: "footer",         init: M_footer },
    { name: "dock",      sel: ".alia-cta-dock", init: M_dock }
  ];

  function mount(mod, within) {
    var root = (within || document).querySelector(mod.sel);
    if (!root) return false;
    killKey(mod.name);
    try { mod.init(root, mod.name); } catch (e) { if (window.console) console.warn("[10-vinyl] " + mod.name, e); }
    return true;
  }

  function mountAll(within) {
    var any = false;
    MODULES.forEach(function (m) { if (mount(m, within)) any = true; });
    return any;
  }

  /* ---- MAIN INIT ---- */
  function init() {
    resolve();
    initGlobals();
    buildGrooves(document);
    mountAll(document);
    sweepUndecorated(document);
    BOOTED = true;
    if (HAS_ST) ScrollTrigger.refresh();
  }

  /* ---- theme editor: inject / reorder / remove a section ---- */
  document.addEventListener("shopify:section:load", function (e) {
    var scope = e.target && e.target.querySelector ? e.target : document;
    resolve();
    buildGrooves(scope);
    MODULES.forEach(function (m) { mount(m, scope); });
    sweepUndecorated(scope);
    dismissPreloader();
    resolve();
    if (HAS_ST) ScrollTrigger.refresh();
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var scope = e.target && e.target.querySelector ? e.target : null;
    if (!scope) return;
    MODULES.forEach(function (m) { if (scope.querySelector(m.sel)) killKey(m.name); });
    resolve();
    if (HAS_ST) ScrollTrigger.refresh();
  });

  document.addEventListener("shopify:section:reorder", function () {
    resolve();
    if (HAS_ST) ScrollTrigger.refresh();
  });

  document.addEventListener("shopify:block:select", function () {
    if (HAS_ST) ScrollTrigger.refresh();
  });

  /* ════════ BOOT ════════ */
  function boot() { resolve(); runPreloader(init); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
